import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Database,
  ExternalLink,
  LineChart,
  Newspaper,
  RefreshCw,
  Search,
  Target,
  X
} from "lucide-react";
import { fetchNewsSources, fetchPremarket, fetchStock, fetchStockDetail, login, verifyAuth, setUnauthorizedHandler } from "./api";
import type { Provider, StockDetail as StockDetailData } from "./api";
import type { Candidate, NewsSource, PremarketPayload, ScoreComponents } from "./types";

type ScoreComponentMeta = {
  key: keyof ScoreComponents;
  label: string;
  max: number;
  what: string;
  how: string;
};

// Daytrade scoring model (enter morning / exit same day). Only free Yahoo-derived
// signals are used. Order here = display order in the score explainer.
const scoreComponentMeta: ScoreComponentMeta[] = [
  {
    key: "relativeVolume",
    label: "วอลุ่มผิดปกติ",
    max: 30,
    what: "วันนี้มีเงินซื้อขายมากกว่าค่าเฉลี่ย 20 วันกี่เท่า",
    how: "≥3 เท่า = 30 · ≥2 = 25 · ≥1.5 = 18 · ≥1.2 = 12 · ≥1 = 6 · น้อยกว่านั้น = 2"
  },
  {
    key: "momentum",
    label: "โมเมนตัม / Gap",
    max: 25,
    what: "ราคาเปิดบวกแบบคุมได้ไหม — เหมาะกับการเข้าช่วงเช้า",
    how: "+0.5–3% = 25 (ดีสุด) · +3–5% = 18 · 0–0.5% = 14 · +5–8% = 10 · ติดลบเล็กน้อย = 8 · บวกเกิน 8% (ไล่เกินไป) = 5"
  },
  {
    key: "liquidity",
    label: "สภาพคล่อง",
    max: 20,
    what: "เงินหมุนมากพอให้เข้า-ออกไวภายในวันไหม",
    how: "≥3,000 ลบ. = 20 · ≥1,000 = 16 · ≥500 = 12 · ≥200 = 8 · ≥50 = 4"
  },
  {
    key: "breakout",
    label: "ใกล้ทะลุแนวต้าน",
    max: 15,
    what: "ราคาอยู่ใกล้/เหนือแนวต้าน 20 วัน (จุดเบรกเอาท์เช้า)",
    how: "ทะลุแล้ว = 15 · ห่าง ≤1% = 12 · ≤3% = 9 · ≤6% = 5"
  },
  {
    key: "volatilityFit",
    label: "ช่วงแกว่งเหมาะ",
    max: 10,
    what: "แกว่งกว้างพอเก็บกำไรไว แต่ไม่เหวี่ยงจนคุมไม่ได้",
    how: "3–8% = 10 (กำลังดี) · 8–12% = 7 · 1.5–3% = 6 · 12–20% = 4 · นิ่งเกินไป = 2"
  }
];

const componentLabels = Object.fromEntries(scoreComponentMeta.map((meta) => [meta.key, meta.label])) as Record<keyof ScoreComponents, string>;
const componentMax = Object.fromEntries(scoreComponentMeta.map((meta) => [meta.key, meta.max])) as Record<keyof ScoreComponents, number>;

function formatPrice(value: number) {
  return value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function latestPrice(stock: Candidate) {
  return stock.market.latest ?? stock.preopen.indicativeOpen;
}

function openPrice(stock: Candidate) {
  return stock.market.open ?? stock.preopen.indicativeOpen;
}

function latestHigh(stock: Candidate) {
  return stock.market.latestHigh ?? stock.market.prevHigh;
}

function latestVolume(stock: Candidate) {
  return stock.market.latestVolume ?? 0;
}

function formatShareVolume(value: number) {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toLocaleString("th-TH", { maximumFractionDigits: 2 })}M หุ้น`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toLocaleString("th-TH", { maximumFractionDigits: 0 })}K หุ้น`;
  }

  return `${value.toLocaleString("th-TH", { maximumFractionDigits: 0 })} หุ้น`;
}

function getPreopenBuyVolumeK(stock: Candidate) {
  if (stock.preopen.buyVolumeK === null) {
    return null;
  }
  if (typeof stock.preopen.buyVolumeK === "number") {
    return stock.preopen.buyVolumeK;
  }

  return (stock.preopen.bidValueTopM * 1000) / Math.max(stock.preopen.indicativeOpen, 0.01);
}

function formatVolumeK(value: number | null) {
  if (value === null) {
    return "ไม่มีข้อมูล";
  }

  if (value >= 1000) {
    return `${(value / 1000).toLocaleString("th-TH", { maximumFractionDigits: 2 })}M หุ้น`;
  }

  return `${value.toLocaleString("th-TH", { maximumFractionDigits: 0 })}K หุ้น`;
}

function volumeRatio(stock: Candidate) {
  return stock.market.value1dM / Math.max(stock.market.avgValue20dM, 0.01);
}

function formatRatio(value: number) {
  return `${value.toFixed(2)}x`;
}

function formatMillionBaht(value: number) {
  return `${value.toLocaleString("th-TH", { maximumFractionDigits: 0 })} ลบ.`;
}

function formatSignedPercent(value: number) {
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(2)}%`;
}

function formatPercent(value: number) {
  return `${value.toFixed(1)}%`;
}

function gapClass(value: number) {
  if (value > 0) return "delta-up";
  if (value < 0) return "delta-down";
  return "";
}

function scoreClass(score: number) {
  if (score >= 80) return "score score-a";
  if (score >= 65) return "score score-b";
  if (score >= 50) return "score score-c";
  return "score score-d";
}

function actionLabel(score: number) {
  if (score >= 80) return "โฟกัส";
  if (score >= 65) return "รอยืนยัน";
  if (score >= 50) return "เฝ้าดู";
  return "ข้าม";
}

// Auth gate. The cookie (set by POST /api/login) is the source of truth — it's
// verified against the server on load, NOT trusted from localStorage. A 401 from
// any API call also drops straight back to the login screen.
export function AuthGate() {
  const [status, setStatus] = useState<"checking" | "authed" | "guest">("checking");

  useEffect(() => {
    let active = true;
    setUnauthorizedHandler(() => {
      if (active) setStatus("guest");
    });
    verifyAuth().then((ok) => {
      if (active) setStatus(ok ? "authed" : "guest");
    });
    return () => {
      active = false;
    };
  }, []);

  if (status === "checking") {
    return (
      <main className="auth-shell">
        <div className="charts-loading">กำลังตรวจสอบสิทธิ์…</div>
      </main>
    );
  }
  if (status === "authed") return <App />;
  return <LoginGate onSuccess={() => setStatus("authed")} />;
}

function LoginGate({ onSuccess }: { onSuccess: () => void }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(false);
    try {
      const ok = await login(code.trim());
      if (ok) {
        onSuccess();
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="auth-shell">
      <form className="auth-card" onSubmit={submit}>
        <div className="brand-mark">
          <LineChart size={26} aria-hidden="true" />
        </div>
        <h1>Hoonpnoi Premarket</h1>
        <p>กรอกรหัสเพื่อเข้าใช้งาน</p>
        <input
          type="password"
          inputMode="numeric"
          autoComplete="current-password"
          autoFocus
          value={code}
          onChange={(event) => {
            setCode(event.target.value);
            setError(false);
          }}
          placeholder="รหัสผ่าน"
        />
        {error ? <span className="auth-error">รหัสไม่ถูกต้อง ลองใหม่อีกครั้ง</span> : null}
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "กำลังตรวจสอบ…" : "เข้าสู่ระบบ"}
        </button>
      </form>
    </main>
  );
}

export function App() {
  const [activeView, setActiveView] = useState<"stocks" | "news">("stocks");
  const [provider, setProvider] = useState<Provider>("set");
  const [payload, setPayload] = useState<PremarketPayload | null>(null);
  const [newsSources, setNewsSources] = useState<NewsSource[]>([]);
  const [selectedSymbol, setSelectedSymbol] = useState<string>("");
  const [dialogStock, setDialogStock] = useState<Candidate | null>(null);
  const [scoreInfoStock, setScoreInfoStock] = useState<Candidate | null>(null);
  const [caStock, setCaStock] = useState<Candidate | null>(null);
  const [symbolQuery, setSymbolQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isNewsLoading, setIsNewsLoading] = useState(false);
  const [isSymbolLoading, setIsSymbolLoading] = useState(false);
  const [error, setError] = useState<string>("");

  const loadData = async () => {
    setIsLoading(true);
    setError("");
    try {
      const data = await fetchPremarket(provider);
      setPayload(data);
      setSelectedSymbol(data.shortlist[0]?.symbol || data.candidates[0]?.symbol || "");
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "โหลดข้อมูลไม่สำเร็จ");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [provider]);

  const loadNewsSources = async () => {
    setIsNewsLoading(true);
    setError("");
    try {
      const data = await fetchNewsSources();
      setNewsSources(data.sources);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "โหลดแหล่งข่าวไม่สำเร็จ");
    } finally {
      setIsNewsLoading(false);
    }
  };

  useEffect(() => {
    if (activeView === "news") {
      void loadNewsSources();
    }
  }, [activeView]);

  const lookupSymbol = async () => {
    const query = symbolQuery.trim().toUpperCase().replace(/\.BK$/, "");
    if (!query) return;

    setIsSymbolLoading(true);
    setError("");
    try {
      const result = await fetchStock(query);
      const stock = result.stock;
      setPayload((current) => {
        if (!current) {
          return {
            tradingDay: new Date().toISOString().slice(0, 10),
            generatedAt: result.generatedAt,
            dataAsOf: stock.dataTime ?? result.generatedAt,
            sourceMode: "yahoo",
            marketBias: "single-symbol",
            dataCoverage: {
              officialPublicStats: false,
              nvdr: false,
              shortSelling: false,
              livePreopenFeed: false
            },
            cues: [
              { label: "Source", value: "Yahoo .BK", tone: "neutral" },
              { label: "Mode", value: "รายตัว", tone: "mixed" },
              { label: "Pre-open", value: "ไม่มี order book", tone: "mixed" },
              { label: "Symbol", value: stock.symbol, tone: "positive" }
            ],
            flowSummary: {
              foreignNetM: 0,
              institutionNetM: 0,
              proprietaryNetM: 0,
              retailNetM: 0,
              programTradingPct: 0
            },
            notes: [
              "Yahoo Finance เป็นแหล่งข้อมูล unofficial/delayed สำหรับราคาและ volume หุ้นไทย .BK",
              "Provider นี้ไม่มีข้อมูล pre-open auction, bid/offer queue, NVDR, short selling หรือ investor type"
            ],
            candidates: [{ ...stock, rank: 1 }],
            shortlist: [{ ...stock, rank: 1 }]
          };
        }

        const merge = (items: Candidate[]) =>
          [stock, ...items.filter((item) => item.symbol !== stock.symbol)].map((item, index) => ({
            ...item,
            rank: index + 1
          }));

        return {
          ...current,
          generatedAt: result.generatedAt,
          candidates: merge(current.candidates),
          shortlist: merge(current.shortlist).slice(0, 5)
        };
      });
      setSelectedSymbol(stock.symbol);
      setDialogStock(stock);
      setSymbolQuery(stock.symbol);
    } catch (lookupError) {
      setError(lookupError instanceof Error ? lookupError.message : "ค้นหาหุ้นไม่สำเร็จ");
    } finally {
      setIsSymbolLoading(false);
    }
  };

  const handleSymbolSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void lookupSymbol();
  };

  const selected = useMemo(() => {
    const candidates = payload?.candidates ?? [];
    return candidates.find((stock) => stock.symbol === selectedSymbol) ?? candidates[0];
  }, [payload, selectedSymbol]);

  const openStockDialog = (stock: Candidate) => {
    setSelectedSymbol(stock.symbol);
    setDialogStock(stock);
  };

  const refreshActiveView = () => {
    if (activeView === "news") {
      void loadNewsSources();
      return;
    }
    void loadData();
  };

  return (
    <main className="app-shell">
      <header className="topbar">
        <div className="brand">
          <div className="brand-mark">
            <LineChart size={22} aria-hidden="true" />
          </div>
          <div>
            <h1>Hoonpnoi Premarket</h1>
            <p>Thai stock technical shortlist</p>
          </div>
        </div>

        <div className="top-actions">
          <form className="symbol-search" onSubmit={handleSymbolSubmit}>
            <label htmlFor="symbol-search">ค้นหุ้น</label>
            <div>
              <Search size={16} aria-hidden="true" />
              <input
                id="symbol-search"
                value={symbolQuery}
                onChange={(event) => setSymbolQuery(event.target.value.toUpperCase())}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    void lookupSymbol();
                  }
                }}
                placeholder="PTT"
                autoComplete="off"
                inputMode="text"
              />
              <button type="submit" disabled={isSymbolLoading} title="ค้นหาหุ้นรายตัว">
                {isSymbolLoading ? <RefreshCw size={16} aria-hidden="true" /> : <Target size={16} aria-hidden="true" />}
              </button>
            </div>
          </form>

          {activeView === "stocks" ? (
            <div className="segmented" role="group" aria-label="data source">
              <button
                className={provider === "set" ? "active" : ""}
                title="ข้อมูล SET (Settrade) ใกล้เรียลไทม์ + order book + NVDR — สแกนทั้งตลาด active"
                onClick={() => setProvider("set")}
              >
                Settrade
              </button>
              <button
                className={provider === "yahoo" ? "active" : ""}
                title="Yahoo Finance (.BK, delayed) — watchlist คงที่"
                onClick={() => setProvider("yahoo")}
              >
                Yahoo
              </button>
            </div>
          ) : null}

          <div className="segmented" role="group" aria-label="view">
            <button
              className={activeView === "stocks" ? "active" : ""}
              title="ตารางหุ้นคัดกรอง"
              onClick={() => setActiveView("stocks")}
            >
              <Database size={16} aria-hidden="true" />
              ทั้งหมด
            </button>
            <button
              className={activeView === "news" ? "active" : ""}
              title="ดูแหล่งข่าวที่ AI ใช้ประกอบการวิเคราะห์"
              onClick={() => setActiveView("news")}
            >
              <Newspaper size={16} aria-hidden="true" />
              แหล่งข่าว
            </button>
          </div>

          <button className="icon-button" title="ดึงข้อมูลใหม่" onClick={refreshActiveView} disabled={isLoading || isNewsLoading}>
            <RefreshCw size={18} aria-hidden="true" />
          </button>
        </div>
      </header>

      {error ? (
        <section className="notice error">
          <AlertTriangle size={18} aria-hidden="true" />
          {error}
        </section>
      ) : null}

      {activeView === "news" ? (
        <NewsSourcesView sources={newsSources} isLoading={isNewsLoading} />
      ) : payload ? (
        <>
          {payload.apiStatus ? (
            <section className="notice">
              <AlertTriangle size={18} aria-hidden="true" />
              {payload.apiStatus}
            </section>
          ) : null}

          <section className="table-section">
            <SectionTitle
              icon={<Search size={18} />}
              title="Candidate Scan"
              meta={`${payload.candidates.length} ตัว · ข้อมูล ณ ${formatDataTime(payload.dataAsOf ?? payload.generatedAt)}${payload.sourceMode === "yahoo" ? " (ดีเลย์)" : ""}`}
            />
            <CandidateTable candidates={payload.candidates} onSelect={openStockDialog} onScoreInfo={setScoreInfoStock} onCaInfo={setCaStock} selectedSymbol={selected?.symbol ?? ""} onSortChange={() => void loadData()} dataAsOf={payload.dataAsOf} />
          </section>

          {dialogStock ? <StockDialog stock={dialogStock} onClose={() => setDialogStock(null)} /> : null}
          {scoreInfoStock ? <ScoreInfoDialog stock={scoreInfoStock} onClose={() => setScoreInfoStock(null)} /> : null}
          {caStock ? <CaDialog stock={caStock} onClose={() => setCaStock(null)} /> : null}
        </>
      ) : (
        <section className="loading-state">
          <RefreshCw size={22} aria-hidden="true" />
          Loading market data
        </section>
      )}
    </main>
  );
}

function NewsSourcesView({ sources, isLoading }: { sources: NewsSource[]; isLoading: boolean }) {
  return (
    <section className="table-section">
      <SectionTitle icon={<Newspaper size={18} />} title="แหล่งข่าว" meta={isLoading ? "loading" : `${sources.length} sources`} />
      <div className="source-table">
        {sources.map((source) => (
          <article className="source-row" key={source.id}>
            <div>
              <a href={source.url} target="_blank" rel="noreferrer">
                {source.name}
                <ExternalLink size={14} aria-hidden="true" />
              </a>
              <p>{source.description}</p>
            </div>
            <div className="source-meta">
              <span>{source.category}</span>
              <strong>{source.enabled ? "enabled" : "disabled"}</strong>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function SectionTitle({ icon, title, meta }: { icon: React.ReactNode; title: string; meta?: string }) {
  return (
    <div className="section-title">
      <div>
        {icon}
        <h2>{title}</h2>
      </div>
      {meta ? <span>{meta}</span> : null}
    </div>
  );
}

function StockDialog({ stock, onClose }: { stock: Candidate; onClose: () => void }) {
  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <section className="stock-dialog" role="dialog" aria-modal="true" aria-labelledby="stock-dialog-title" onClick={(event) => event.stopPropagation()}>
        <button className="dialog-close" title="ปิด" onClick={onClose}>
          <X size={18} aria-hidden="true" />
        </button>
        <StockDetail stock={stock} />
      </section>
    </div>
  );
}

function ScoreInfoDialog({ stock, onClose }: { stock: Candidate; onClose: () => void }) {
  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <section className="stock-dialog" role="dialog" aria-modal="true" aria-labelledby="score-info-title" onClick={(event) => event.stopPropagation()}>
        <button className="dialog-close" title="ปิด" onClick={onClose}>
          <X size={18} aria-hidden="true" />
        </button>
        <div className="detail-content">
          <div className="detail-head">
            <div>
              <span>{stock.symbol} · วิธีคิดคะแนน</span>
              <h2 id="score-info-title">{stock.score}/100</h2>
            </div>
            <div className={scoreClass(stock.score)}>{stock.scoreBand}</div>
          </div>

          <p className="score-intro">
            คะแนนนี้ออกแบบเพื่อ <strong>เดย์เทรด: เข้าช่วงเช้า–ออกภายในวัน</strong> จึงให้น้ำหนักเฉพาะสัญญาณที่ดูได้จากข้อมูลฟรี
            (วอลุ่ม ราคา การแกว่ง) ไม่รวม flow/NVDR ที่ต้องต่อข้อมูลพิเศษ รวมเต็ม 100 คะแนน — ยิ่งสูงยิ่งน่าโฟกัสตอนเปิด
          </p>

          <div className="component-list">
            {scoreComponentMeta.map((meta) => {
              const value = stock.components[meta.key];
              return (
                <div className="score-factor" key={meta.key}>
                  <div className="score-bar-label">
                    <span>{meta.label}</span>
                    <strong>{value}/{meta.max}</strong>
                  </div>
                  <div className="bar-track">
                    <div style={{ width: `${(value / meta.max) * 100}%` }} />
                  </div>
                  <p className="score-what">{meta.what}</p>
                  <p className="score-how">เกณฑ์: {meta.how}</p>
                </div>
              );
            })}
          </div>

          <div className="decision-row">
            <span>{actionLabel(stock.score)}</span>
            <strong>{stock.scoreBand}</strong>
          </div>

          <p className="score-foot">รวม {stock.score}/100 · A-list ≥80 · Confirm 65–79 · Watch 50–64 · ต่ำกว่า 50 ข้าม</p>
        </div>
      </section>
    </div>
  );
}

function buildLinePath(values: number[], x: (i: number) => number, y: (v: number) => number) {
  return values.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
}

// ---------- Corporate Action (XD) ----------

const CA_TYPE_LABELS: Record<string, string> = {
  XD: "ไม่ได้สิทธิเงินปันผล",
  "XD(ST)": "ไม่ได้สิทธิปันผล (จ่ายเป็นหุ้น)",
  XR: "ไม่ได้สิทธิจองหุ้นเพิ่มทุน",
  XM: "ไม่มีสิทธิเข้าประชุมผู้ถือหุ้น",
  XB: "ไม่ได้สิทธิประโยชน์อื่น",
  XN: "ไม่ได้สิทธิรับเงินคืนทุน",
  XW: "ไม่ได้สิทธิรับใบสำคัญแสดงสิทธิ",
  XE: "ไม่มีสิทธิแปลงสภาพ"
};
const caTypeLabel = (type: string) => CA_TYPE_LABELS[type] ?? "";

function formatThaiDate(value: string | null | undefined) {
  if (!value) return "-";
  const date = new Date(`${value}T00:00:00+07:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("th-TH-u-ca-gregory", { day: "numeric", month: "short", year: "2-digit" }).format(date);
}

// Bangkok "today" as YYYY-MM-DD, independent of the viewer's timezone.
const bangkokTodayKey = () => new Date(Date.now() + 7 * 3_600_000).toISOString().slice(0, 10);

function XdBadge({ stock, onClick }: { stock: Candidate; onClick: (stock: Candidate) => void }) {
  const xd = stock.ca?.xd;
  if (!xd) return null;
  return (
    <button
      type="button"
      className={`xd-badge ${xd.isToday ? "xd-today" : "xd-upcoming"}`}
      title={xd.isToday ? "ขึ้นเครื่องหมาย XD วันนี้ — แตะดูรายละเอียด" : `จะขึ้น XD ${formatThaiDate(xd.date)} — แตะดูรายละเอียด`}
      onClick={(event) => {
        event.stopPropagation();
        onClick(stock);
      }}
    >
      XD
    </button>
  );
}

function CaDialog({ stock, onClose }: { stock: Candidate; onClose: () => void }) {
  const ca = stock.ca;
  const xd = ca?.xd ?? null;
  const others = (ca?.upcoming ?? []).filter((entry) => !String(entry.caType).startsWith("XD"));
  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <section className="stock-dialog ca-dialog" role="dialog" aria-modal="true" aria-labelledby="ca-dialog-title" onClick={(event) => event.stopPropagation()}>
        <button className="dialog-close" title="ปิด" onClick={onClose}>
          <X size={18} aria-hidden="true" />
        </button>
        <div className="detail-content">
          <div className="detail-head">
            <div>
              <span>{stock.name}</span>
              <h2 id="ca-dialog-title">{stock.symbol} · เครื่องหมาย CA</h2>
            </div>
          </div>

          {xd ? (
            <div className={`ca-hero ${xd.isToday ? "ca-hero-today" : ""}`}>
              <div className="ca-hero-top">
                <strong>XD</strong>
                <span>{xd.isToday ? "ขึ้นเครื่องหมายวันนี้" : xd.daysUntil === 1 ? "พรุ่งนี้" : `อีก ${xd.daysUntil} วัน`}</span>
              </div>
              <div className="ca-hero-grid">
                <InfoBox label="วันขึ้น XD" value={formatThaiDate(xd.date)} />
                <InfoBox label="เงินปันผล" value={xd.dividend !== null ? `${xd.dividend} บาท/หุ้น` : "รอประกาศ"} />
                <InfoBox label="ประเภท" value={xd.dividendType ?? "-"} />
                <InfoBox label="วันจ่ายปันผล" value={formatThaiDate(xd.paymentDate)} />
              </div>
              <p className="ca-hero-note">
                ซื้อตั้งแต่วันขึ้น XD จะไม่ได้ปันผลรอบนี้ และราคามักถูกปรับลงราวเงินปันผลในวัน XD — Gap ลบวันนั้นไม่ได้แปลว่าหุ้นอ่อน
              </p>
            </div>
          ) : (
            <p className="ca-empty">ไม่มี XD ที่กำลังจะถึงในปฏิทิน SET (เดือนนี้ + เดือนหน้า)</p>
          )}

          {others.length ? (
            <div className="ca-other">
              <h3>เครื่องหมายอื่นที่กำลังจะถึง</h3>
              {others.map((entry) => (
                <div className="ca-row" key={`${entry.caType}-${entry.date}`}>
                  <span className="ca-chip">{entry.caType}</span>
                  <span>{formatThaiDate(entry.date)}</span>
                  <small>{caTypeLabel(entry.caType)}</small>
                </div>
              ))}
            </div>
          ) : null}

          {ca?.sign ? <p className="ca-sign-note">เครื่องหมายบนกระดานวันนี้: {ca.sign}</p> : null}
          <p className="ca-source-note">ที่มา: ปฏิทิน X-Calendar ของ set.or.th · อัปเดตราว 12 ชม./ครั้ง</p>
        </div>
      </section>
    </div>
  );
}

function IntradayChart({ intraday, priorClose }: { intraday: StockDetailData["intraday"]; priorClose: number }) {
  if (!intraday.length) return <div className="chart-empty">ไม่มีข้อมูล intraday</div>;
  const W = 320;
  const PH = 88;
  const VH = 24;
  const PAD = 5;
  const prices = intraday.map((p) => p.price);
  const vwaps = intraday.map((p) => p.vwap);
  const lo = Math.min(priorClose, ...prices, ...vwaps);
  const hi = Math.max(priorClose, ...prices, ...vwaps);
  const span = Math.max(hi - lo, 0.01);
  const n = intraday.length;
  const x = (i: number) => (n <= 1 ? 0 : (i / (n - 1)) * W);
  const y = (v: number) => PAD + (PH - 2 * PAD) - ((v - lo) / span) * (PH - 2 * PAD);
  const vMax = Math.max(1, ...intraday.map((p) => p.volume));
  const priorY = y(priorClose);
  const baseY = PH + VH + 6;

  return (
    <svg className="chart-svg" viewBox={`0 0 ${W} ${baseY}`} preserveAspectRatio="none" role="img" aria-label="intraday price">
      <line x1="0" y1={priorY} x2={W} y2={priorY} className="chart-prior" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
      {intraday.map((p, i) => {
        const h = (p.volume / vMax) * VH;
        return <rect key={i} x={x(i)} y={baseY - h} width={Math.max(0.4, W / n - 0.2)} height={h} className="chart-vol" />;
      })}
      <path d={buildLinePath(vwaps, x, y)} className="chart-vwap" fill="none" strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
      <path d={buildLinePath(prices, x, y)} className="chart-price" fill="none" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function DailyChart({ daily, resistance1, support1 }: { daily: StockDetailData["daily"]; resistance1: number; support1: number }) {
  if (!daily.length) return <div className="chart-empty">ไม่มีข้อมูลรายวัน</div>;
  const W = 320;
  const H = 96;
  const PAD = 5;
  const levels = [resistance1, support1].filter((v) => v > 0);
  const lo = Math.min(...daily.map((d) => d.low), ...levels);
  const hi = Math.max(...daily.map((d) => d.high), ...levels);
  const span = Math.max(hi - lo, 0.01);
  const n = daily.length;
  const x = (i: number) => (n <= 1 ? 0 : (i / (n - 1)) * W);
  const y = (v: number) => PAD + (H - 2 * PAD) - ((v - lo) / span) * (H - 2 * PAD);

  return (
    <svg className="chart-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="daily close">
      {resistance1 > 0 ? <line x1="0" y1={y(resistance1)} x2={W} y2={y(resistance1)} className="chart-res" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" /> : null}
      {support1 > 0 ? <line x1="0" y1={y(support1)} x2={W} y2={y(support1)} className="chart-sup" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" /> : null}
      <path d={buildLinePath(daily.map((d) => d.close), x, y)} className="chart-price" fill="none" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function Readout({ label, value, tone }: { label: string; value: string; tone?: "up" | "down" | "neutral" }) {
  return (
    <div className={`readout readout-${tone ?? "neutral"}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

type Verdict = { tone: "good" | "ok" | "wait" | "avoid"; headline: string; why: string; caveat: string | null };

// Plain-language buy/skip verdict for non-technical users.
function buildVerdict(stock: Candidate, vsVwapPct: number | null, rangePos: number | null): Verdict {
  // On XD day judge momentum on the dividend-adjusted gap — the raw markdown is mechanical, not weakness.
  const xd = stock.ca?.xd;
  const xdAdjust = xd?.isToday && xd.dividend !== null ? (xd.dividend / Math.max(stock.market.prevClose, 0.01)) * 100 : 0;
  const gap = stock.preopen.indicativeGapPct + xdAdjust;
  const ratio = volumeRatio(stock);
  const pros: string[] = [];
  const cons: string[] = [];

  if (gap >= 0.5) pros.push("เปิดบวกมีแรงส่ง" + (xdAdjust > 0 ? " (หลังปรับคืนปันผล XD)" : ""));
  else if (gap < 0) cons.push("ราคายังติดลบ" + (xdAdjust > 0 ? "แม้ปรับคืนปันผล XD แล้ว" : ""));
  if (ratio >= 1.5) pros.push("คนแห่ซื้อขายมากกว่าปกติ");
  else if (ratio < 1.2) cons.push("คนยังไม่ค่อยสนใจ (วอลุ่มปกติ)");
  if (rangePos !== null && rangePos >= 80) pros.push("ราคาใกล้ทะลุแนวต้าน");
  if (vsVwapPct !== null) {
    if (vsVwapPct >= 0) pros.push("ราคายืนเหนือ VWAP (แรงซื้อยังคุม)");
    else cons.push("ราคาหลุด VWAP (เริ่มอ่อนแรง)");
  }

  const score = stock.score;
  if (score >= 80) {
    return {
      tone: "good",
      headline: "น่าสนใจ — เข้าซื้อช่วงเช้าได้",
      why: `เพราะ ${pros.slice(0, 3).join(" · ") || "สัญญาณโดยรวมแข็งแรง"}`,
      caveat: "⚠️ เป็นการเก็งกำไรระยะสั้น — ตั้งจุดขายไว้ก่อน แล้วรีบขายภายในวัน ไม่ถือข้ามคืน"
    };
  }
  if (score >= 65) {
    return {
      tone: "ok",
      headline: "พอเข้าได้ — แต่ต้องรอให้วอลุ่มยืนยันหลังเปิด",
      why: `${pros.length ? "จุดเด่น: " + pros.slice(0, 2).join(" · ") : ""}${cons.length ? " · ต้องระวัง: " + cons[0] : ""}`.trim() || "สัญญาณก้ำกึ่ง",
      caveat: "⚠️ ถ้าเปิดมาแล้ววอลุ่มไม่ตาม อย่าเข้า — และถ้าเข้าแล้วต้องรีบขายภายในวัน"
    };
  }
  if (score >= 50) {
    return {
      tone: "wait",
      headline: "ยังไม่ควรซื้อ — เฝ้าดูก่อน",
      why: `เพราะ ${cons.slice(0, 2).join(" · ") || "สัญญาณยังไม่ชัดพอ"}`,
      caveat: null
    };
  }
  return {
    tone: "avoid",
    headline: "ไม่ควรซื้อวันนี้",
    why: `เพราะ ${cons.slice(0, 2).join(" · ") || "สัญญาณอ่อน เสี่ยงเกินคุ้ม"}`,
    caveat: null
  };
}

function StockCharts({ stock }: { stock: Candidate }) {
  const [detail, setDetail] = useState<StockDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setError("");
    fetchStockDetail(stock.symbol)
      .then((data) => active && setDetail(data))
      .catch((loadError) => active && setError(loadError instanceof Error ? loadError.message : "โหลดกราฟไม่สำเร็จ"))
      .finally(() => active && setIsLoading(false));
    return () => {
      active = false;
    };
  }, [stock.symbol]);

  if (isLoading) return <div className="charts-loading">กำลังโหลดกราฟจาก SET…</div>;
  if (error || !detail) return <div className="charts-loading">กราฟไม่พร้อม{error ? `: ${error}` : ""}</div>;

  const { support1, resistance1 } = stock.technical;
  const last = detail.last || stock.market.latest || 0;
  const vwap = detail.vwapLast;
  const vsVwapPct = vwap ? ((last - vwap) / vwap) * 100 : null;
  const rangePos = resistance1 > support1 ? Math.max(0, Math.min(100, ((last - support1) / (resistance1 - support1)) * 100)) : null;
  const ob = detail.orderBook;
  const imbalance = detail.live && ob.bidVolume + ob.offerVolume > 0 ? (ob.bidVolume / (ob.bidVolume + ob.offerVolume)) * 100 : null;
  const verdict = buildVerdict(stock, vsVwapPct, rangePos);

  return (
    <div className="stock-charts">
      <div className="readout-row">
        <Readout label="สถานะตลาด" value={detail.marketStatus} tone={detail.live ? "up" : "neutral"} />
        {vsVwapPct !== null ? (
          <Readout
            label="ราคา vs VWAP"
            value={`${vsVwapPct >= 0 ? "เหนือ" : "ใต้"} ${Math.abs(vsVwapPct).toFixed(2)}%`}
            tone={vsVwapPct >= 0 ? "up" : "down"}
          />
        ) : null}
        {rangePos !== null ? (
          <Readout label="ตำแหน่งในกรอบ 20 วัน" value={`${rangePos.toFixed(0)}%`} tone={rangePos >= 80 ? "up" : "neutral"} />
        ) : null}
        {imbalance !== null ? (
          <Readout label="แรงซื้อคิว (best bid)" value={`${imbalance.toFixed(0)}%`} tone={imbalance >= 55 ? "up" : imbalance <= 45 ? "down" : "neutral"} />
        ) : null}
      </div>

      <div className="chart-block">
        <div className="chart-head">
          <span>ราคาในวัน (intraday)</span>
          <small><b className="dot-price" /> ราคา · <b className="dot-vwap" /> VWAP · <b className="dot-prior" /> ปิดก่อนหน้า</small>
        </div>
        <IntradayChart intraday={detail.intraday} priorClose={detail.priorClose} />
        <p className="chart-note">
          เส้นเขียว = ราคา · เส้นส้ม = ราคาเฉลี่ยของวัน (VWAP). ราคา<b>อยู่เหนือ</b>เส้นส้ม = คนยังไล่ซื้อ (ดี) ·
          <b>ต่ำกว่า</b>เส้นส้ม = แรงซื้อเริ่มหาย (ระวัง)
        </p>
      </div>

      <div className="chart-block">
        <div className="chart-head">
          <span>ราคา 40 วัน + แนวรับ/ต้าน</span>
          <small><b className="dot-res" /> ต้าน {resistance1.toFixed(2)} · <b className="dot-sup" /> รับ {support1.toFixed(2)}</small>
        </div>
        <DailyChart daily={detail.daily} resistance1={resistance1} support1={support1} />
        <p className="chart-note">
          เส้นแดง = แนวต้าน (ราคามักโดนกดลงตรงนี้) · เส้นเขียว = แนวรับ. ราคา<b>ชนเส้นแดงแล้วทะลุขึ้น</b> = สัญญาณวิ่งต่อ
        </p>
      </div>

      <div className={`verdict verdict-${verdict.tone}`}>
        <h4>{verdict.headline}</h4>
        <p>{verdict.why}</p>
        {verdict.caveat ? <p className="verdict-caveat">{verdict.caveat}</p> : null}
      </div>

      {detail.corporateActions?.length ? <CaHistory detail={detail} /> : null}
    </div>
  );
}

function CaHistory({ detail }: { detail: StockDetailData }) {
  const todayKey = bangkokTodayKey();
  const entries = (detail.corporateActions ?? []).slice(0, 12);
  const xd = detail.ca?.xd ?? null;
  return (
    <div className="chart-block ca-history">
      <div className="chart-head">
        <span>Corporate Actions</span>
        {xd ? (
          <small className={xd.isToday ? "ca-flag-today" : "ca-flag-upcoming"}>
            XD {xd.isToday ? "วันนี้" : formatThaiDate(xd.date)}
            {xd.dividend !== null ? ` · ${xd.dividend} บ.` : ""}
          </small>
        ) : null}
      </div>
      <div className="ca-table-wrap">
        <table className="ca-table">
          <thead>
            <tr>
              <th>เครื่องหมาย</th>
              <th>วันที่ X</th>
              <th>ปันผล / รายละเอียด</th>
              <th>วันจ่าย</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, index) => {
              const isXd = String(entry.caType).startsWith("XD");
              const isFuture = Boolean(entry.date && entry.date >= todayKey);
              return (
                <tr key={`${entry.caType}-${entry.date}-${index}`} className={`${isXd ? "ca-row-xd" : ""} ${isFuture ? "ca-row-future" : ""}`.trim()}>
                  <td><span className={`ca-chip ${isXd ? "ca-chip-xd" : ""}`}>{entry.caType}</span></td>
                  <td>{formatThaiDate(entry.date)}</td>
                  <td className="ca-detail-cell">
                    {entry.dividend !== null
                      ? `${entry.dividend} บ. ${entry.dividendType === "Stock Dividend" ? "(หุ้น)" : ""}`.trim()
                      : entry.agenda ?? entry.ratio ?? caTypeLabel(entry.caType) ?? "-"}
                  </td>
                  <td>{formatThaiDate(entry.paymentDate)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="chart-note">แถวไฮไลต์ = ยังไม่ถึงวันขึ้นเครื่องหมาย · แสดง 12 รายการล่าสุดจาก set.or.th</p>
    </div>
  );
}

function StockDetail({ stock }: { stock: Candidate }) {
  return (
    <div className="detail-content">
      <div className="detail-head">
        <div>
          <span>{stock.name}</span>
          <h2 id="stock-dialog-title">{stock.symbol}</h2>
        </div>
        <div className={scoreClass(stock.score)}>{stock.score}</div>
      </div>

      <div className="decision-row">
        <span>{actionLabel(stock.score)}</span>
        <strong>{stock.scoreBand}</strong>
      </div>

      <StockCharts stock={stock} />

      <div className="price-grid">
        <PriceBox label="ล่าสุด" value={latestPrice(stock)} />
        <PriceBox label="เปิด" value={openPrice(stock)} />
        <PriceBox label="ปิดก่อนหน้า" value={stock.market.prevClose} />
        <PriceBox label="High ล่าสุด" value={latestHigh(stock)} />
        <PriceBox label="Volume ล่าสุด" valueText={formatShareVolume(latestVolume(stock))} />
        <PriceBox label="High / Low" valueText={`${formatPrice(stock.market.prevHigh)} / ${formatPrice(stock.market.prevLow)}`} />
        <PriceBox label="Trigger" value={stock.plan.trigger} />
        <PriceBox label="Stop" value={stock.plan.stop} />
        <PriceBox label="Target 1" value={stock.plan.target1} />
        <PriceBox label="Target 2" value={stock.plan.target2} />
      </div>

      <div className="preopen-grid">
        <InfoBox label="Buy Volume ก่อนเปิด" value={formatVolumeK(getPreopenBuyVolumeK(stock))} />
        <InfoBox label="Bid Value ก่อนเปิด" value={`${stock.preopen.bidValueTopM.toLocaleString("th-TH")} ลบ.`} />
        <InfoBox label="Indicative Value" value={`${stock.preopen.indicativeValueM.toLocaleString("th-TH")} ลบ.`} />
        <InfoBox label="Queue" value={stock.preopen.queueStability} />
      </div>

      <div className="factor-section">
        <h3>คะแนนแยกตามปัจจัย (เต็ม 100% = ดีสุด)</h3>
        <div className="factor-cards">
          {scoreComponentMeta.map((meta) => {
            const pct = Math.round((stock.components[meta.key] / meta.max) * 100);
            const tone = pct >= 67 ? "good" : pct >= 34 ? "ok" : "bad";
            return (
              <div className={`factor-card factor-${tone}`} key={meta.key}>
                <div className="factor-top">
                  <span>{meta.label}</span>
                  <strong>{pct}%</strong>
                </div>
                <p>{meta.what}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="reason-list">
        <h3>Reasons</h3>
        {stock.reasons.map((reason) => (
          <p key={reason}>{reason}</p>
        ))}
      </div>

      <div className="no-trade">
        <AlertTriangle size={16} aria-hidden="true" />
        <span>{stock.plan.noTrade}</span>
      </div>
    </div>
  );
}

function PriceBox({ label, value, valueText }: { label: string; value?: number; valueText?: string }) {
  return (
    <div className="price-box">
      <span>{label}</span>
      <strong>{valueText ?? formatPrice(value ?? 0)}</strong>
    </div>
  );
}

function InfoBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="info-box">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function ScoreBar({ label, value, max }: { label: string; value: number; max: number }) {
  return (
    <div className="score-bar">
      <div className="score-bar-label">
        <span>{label}</span>
        <strong>
          {value}/{max}
        </strong>
      </div>
      <div className="bar-track">
        <div style={{ width: `${(value / max) * 100}%` }} />
      </div>
    </div>
  );
}

type SortKey = "buyVol" | "gap" | "volRatio" | "value" | "latestVol" | "atr" | "score";

function buyVolSortValue(stock: Candidate) {
  const value = getPreopenBuyVolumeK(stock);
  return value === null ? -Infinity : value;
}

// Sort metrics — all sourced from the free Yahoo feed (no signup), sorted
// descending. buyVol is kept but is empty until a real pre-open feed is wired.
const sortValue: Record<SortKey, (stock: Candidate) => number> = {
  buyVol: buyVolSortValue,
  gap: (stock) => stock.preopen.indicativeGapPct,
  volRatio: volumeRatio,
  value: (stock) => stock.market.value1dM,
  latestVol: latestVolume,
  atr: (stock) => stock.technical.atrPct,
  score: (stock) => stock.score
};

type SortOption = { key: SortKey; label: string; what: string; why: string; watch: string; related: SortKey[] };

// Column + button order. Columns in the table follow this exact order.
// `related` = the columns worth eyeballing alongside this one (highlighted on click).
const sortOptions: SortOption[] = [
  {
    key: "buyVol",
    label: "Buy Vol",
    what: "จำนวนหุ้นที่คนตั้งคิวรอ “ซื้อ” ไว้ตั้งแต่ก่อนตลาดเปิด",
    why: "ยิ่งมีคนแย่งกันซื้อเยอะตั้งแต่ยังไม่เปิด ราคามักจะพุ่งแรงตอนเปิด เป็นสัญญาณว่า “ของกำลังจะมา” เหมาะกับการเตรียมเข้าซื้อเช้าเร็ว",
    watch: "ดูคู่กับ Gap % (ถ้าคิวซื้อเยอะ + เปิดบวก = แรงจริง) และ Vol Ratio. หมายเหตุ: ข้อมูลฟรีจาก Yahoo ยังไม่มีค่านี้ (ขึ้นว่าง) ต้องต่อข้อมูลจริงจากตลาด/โบรกก่อน",
    related: ["gap", "volRatio"]
  },
  {
    key: "gap",
    label: "Gap %",
    what: "วันนี้ราคาสูงหรือต่ำกว่าราคาปิดเมื่อวานกี่เปอร์เซ็นต์",
    why: "บวกพอดีๆ (ราว +0.5% ถึง +3%) แปลว่ามีแรงซื้อหนุน น่าสนใจ; บวกแรงเกินไปคือไล่ราคา เสี่ยงโดนเทขายทำกำไร; ติดลบคือเริ่มอ่อนแรง",
    watch: "ดูคู่กับ Vol Ratio เสมอ — เปิดบวกแต่คนซื้อขายน้อยอาจเป็นของหลอก; เปิดบวก + คนซื้อขายเยอะ = ของจริง. ดู ช่วงแกว่ง % เพื่อกะว่าจะวิ่งไกลแค่ไหน",
    related: ["volRatio", "atr"]
  },
  {
    key: "volRatio",
    label: "Vol Ratio",
    what: "วันนี้มีเงินซื้อขายมากกว่าปกติ (ค่าเฉลี่ย 20 วัน) กี่เท่า เช่น 2x = มากกว่าปกติเท่าตัว",
    why: "เป็นตัวจับ “หุ้นกำลังร้อน/มีคนสนใจผิดปกติ” ที่ดีที่สุดที่ดูได้จากข้อมูลฟรี ตั้งแต่ 2 เท่าขึ้นไปถือว่าน่าจับตาเป็นพิเศษ",
    watch: "ดูคู่กับ Gap % (วอลุ่มพุ่ง + เปิดบวก = แรงซื้อของจริง) และ มูลค่า ฿M (ดูว่าตัวใหญ่พอให้เข้า-ออกสะดวกไหม)",
    related: ["gap", "value"]
  },
  {
    key: "value",
    label: "มูลค่า ฿M",
    what: "เม็ดเงิน (ล้านบาท) ที่ซื้อขายกันในหุ้นตัวนี้",
    why: "บอก “สภาพคล่อง” — ยิ่งเงินหมุนเยอะ ยิ่งเข้าและออกได้ไว ไม่ติดของ สำคัญมากสำหรับเดย์เทรดที่ต้องขายให้ทันภายในวัน",
    watch: "ดูคู่กับ Vol Ratio (มูลค่าสูง + สูงกว่าปกติ = ทั้งคล่องทั้งร้อน) และ Vol ล่าสุด",
    related: ["volRatio", "latestVol"]
  },
  {
    key: "latestVol",
    label: "Vol ล่าสุด",
    what: "จำนวนหุ้น (เป็นจำนวนหุ้น ไม่ใช่เงิน) ที่เพิ่งซื้อขายไปล่าสุด",
    why: "ยืนยันว่ามีการซื้อขายคึกคักจริง คล้าย Vol Ratio แต่เป็นจำนวนหุ้นดิบๆ",
    watch: "ดูคู่กับ มูลค่า ฿M (จำนวนหุ้น × ราคา = เม็ดเงิน) — หุ้นราคาถูกจำนวนหุ้นเยอะแต่เม็ดเงินอาจไม่มาก",
    related: ["value", "volRatio"]
  },
  {
    key: "atr",
    label: "ช่วงแกว่ง %",
    what: "ปกติราคาหุ้นตัวนี้แกว่งขึ้น-ลงกว้างประมาณกี่เปอร์เซ็นต์ (อิงกรอบ 20 วัน)",
    why: "บอกว่าต่อรอบมีโอกาสได้กำไร/ขาดทุนมากแค่ไหน แกว่งกว้าง = กำไรไวแต่เสี่ยงไวด้วย ต้องเผื่อจุดตัดขาดทุนให้กว้างขึ้น",
    watch: "ดูคู่กับ Gap % และจุด Trigger/Stop (กดเข้าไปดูรายตัว) เพื่อตั้งเป้าทำกำไรและจุดหนีให้สมจริง",
    related: ["gap"]
  },
  {
    key: "score",
    label: "Scores",
    what: "คะแนนรวม 0–100 ที่ระบบประมวลจากทุกปัจจัย (สภาพคล่อง วอลุ่ม เทคนิค gap ฯลฯ)",
    why: "สรุปภาพรวมเหลือตัวเลขเดียว: 80+ = น่าโฟกัส, 65–79 = รอวอลุ่มยืนยันหลังเปิด, ต่ำกว่า 50 = ข้ามไปก่อน",
    watch: "คลิกที่ชื่อหุ้นเพื่อเปิดดูว่าคะแนนมาจากปัจจัยไหนบ้าง และดู Vol Ratio + Gap % ประกอบ",
    related: ["volRatio", "gap"]
  }
];

// Per-metric competition ranking (1 = highest value); equal values share a rank.
function buildRanks(candidates: Candidate[], getValue: (stock: Candidate) => number) {
  const ranked = [...candidates].sort((a, b) => getValue(b) - getValue(a));
  const map = new Map<string, number>();
  let prevValue = Number.NaN;
  let prevRank = 0;
  ranked.forEach((stock, index) => {
    const value = getValue(stock);
    const rank = value === prevValue ? prevRank : index + 1;
    prevValue = value;
    prevRank = rank;
    map.set(stock.symbol, rank);
  });
  return map;
}

function RankBadge({ rank }: { rank?: number }) {
  if (!rank) return null;
  const tier = rank === 1 ? "rank-badge-1" : rank <= 3 ? "rank-badge-top" : "";
  return <span className={`rank-badge ${tier}`.trim()}>#{rank}</span>;
}

function buildAiPrompt(rows: Candidate[], sortLabel: string, dataAsOf: string | null | undefined, sources: NewsSource[]) {
  const when = dataAsOf ? formatDataTime(dataAsOf) : "ล่าสุด";
  const xdCell = (stock: Candidate) => {
    const xd = stock.ca?.xd;
    if (!xd) return "-";
    const dividend = xd.dividend !== null ? ` (${xd.dividend}฿)` : "";
    return xd.isToday ? `วันนี้${dividend}` : `${xd.date}${dividend}`;
  };
  const header = "อันดับ | สัญลักษณ์ | คะแนน | Gap% | VolRatio(x20วัน) | มูลค่า(฿ล้าน) | Vol ล่าสุด | ช่วงแกว่ง% | Setup | XD";
  const tableLines = rows
    .map((stock, index) =>
      [
        index + 1,
        stock.symbol,
        stock.score,
        formatSignedPercent(stock.preopen.indicativeGapPct),
        `${volumeRatio(stock).toFixed(2)}x`,
        Math.round(stock.market.value1dM).toLocaleString("en-US"),
        formatShareVolume(latestVolume(stock)),
        `${stock.technical.atrPct.toFixed(1)}%`,
        stock.technical.label,
        xdCell(stock)
      ].join(" | ")
    )
    .join("\n");
  const newsList = sources.length
    ? sources.map((source) => `- ${source.name}: ${source.url}`).join("\n")
    : "- (ยังไม่มีรายการแหล่งข่าวในระบบ)";

  return `คุณคือผู้ช่วยวิเคราะห์หุ้นไทย (SET) สำหรับการ "เดย์เทรด" — กลยุทธ์คือเข้าซื้อช่วงเช้าหลังตลาดเปิด แล้วขายภายในวันเดียว (โดยปกติไม่ถือข้ามคืน)

ด้านล่างคือหุ้น 10 อันดับแรกจากระบบคัดกรอง (เรียงตาม: ${sortLabel}) ข้อมูล ณ ${when}
ความหมายของตัวเลข:
- Gap% = ราคาวันนี้เทียบราคาปิดเมื่อวาน (ค่าดิบ ไม่ได้ปรับปันผล)
- VolRatio = มูลค่าซื้อขายวันนี้เทียบค่าเฉลี่ย 20 วัน (ยิ่งสูง = คนสนใจผิดปกติ)
- มูลค่า = มูลค่าซื้อขาย บอกสภาพคล่อง (เข้า-ออกง่ายแค่ไหน)
- ช่วงแกว่ง% = ความผันผวนปกติของหุ้น
- XD = วันขึ้นเครื่องหมายไม่ได้สิทธิปันผล (พร้อมเงินปันผลต่อหุ้น) — สำคัญ: ในวัน XD ราคาถูกปรับลงราวเงินปันผลโดยกลไกตลาด Gap% ติดลบวันนั้นจึงไม่ใช่สัญญาณอ่อน อย่าตีความผิด

${header}
${tableLines}

สิ่งที่อยากให้ช่วยวิเคราะห์:
1. แต่ละตัวน่าเข้าซื้อช่วงเช้าหรือไม่ เพราะอะไร (อ้างอิงตัวเลขข้างบน)
2. คำเตือนความเสี่ยงของแต่ละตัว
3. เสนอจังหวะเข้า (trigger), จุดขายทำกำไร (target) และจุดตัดขาดทุน (stop)
4. ควรขายภายในวัน หรือถ้าจะถือต่อ ควรถือกี่วัน เพราะอะไร — ตัวที่มี XD ใกล้ถึง ให้ชี้ผลของ XD ต่อจังหวะเข้า-ออกด้วย (ถือข้ามวัน XD ได้ปันผลแต่ราคาจะถูกปรับลงราวปันผล คุ้มหรือไม่)
5. จัดอันดับ top picks ที่น่าสนใจที่สุด พร้อมเหตุผลสั้นๆ

ก่อนสรุป ขอให้ค้นหาข่าว/ปัจจัยล่าสุดของหุ้นแต่ละตัวจาก "แหล่งข่าว" ด้านล่างนี้ (เปิด URL แล้วหาข่าวที่เกี่ยวข้องกับแต่ละสัญลักษณ์) เพื่อใช้ประกอบการวิเคราะห์:
${newsList}

หมายเหตุ: ราคา/วอลุ่ม/NVDR มาจาก SET ใกล้เรียลไทม์ แต่ไม่รวม short-selling และ flow รายนักลงทุนแบบเต็ม — ใช้ประกอบการตัดสินใจเท่านั้น ไม่ใช่คำแนะนำการลงทุน`;
}

function AiPromptDialog({ rows, sortLabel, dataAsOf, onClose }: { rows: Candidate[]; sortLabel: string; dataAsOf: string | null | undefined; onClose: () => void }) {
  const [sources, setSources] = useState<NewsSource[]>([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    fetchNewsSources()
      .then((data) => active && setSources(data.sources))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const prompt = buildAiPrompt(rows, sortLabel, dataAsOf, sources);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard may be blocked — user can still select the textarea manually
    }
  };

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <section className="stock-dialog" role="dialog" aria-modal="true" aria-labelledby="ai-prompt-title" onClick={(event) => event.stopPropagation()}>
        <button className="dialog-close" title="ปิด" onClick={onClose}>
          <X size={18} aria-hidden="true" />
        </button>
        <div className="detail-content">
          <div className="detail-head">
            <div>
              <span>คัดลอกไปให้ AI วิเคราะห์ต่อ</span>
              <h2 id="ai-prompt-title">ข้อความสำหรับ AI</h2>
            </div>
          </div>
          <p className="ai-prompt-note">
            กด "คัดลอกข้อความ" แล้วนำไปวางในแชต AI (เช่น ChatGPT / Claude) — มีหุ้น 10 อันดับแรก + รายการแหล่งข่าวแนบให้ AI ไปหาข่าวต่อ
          </p>
          <textarea className="ai-prompt-text" readOnly rows={15} value={prompt} onFocus={(event) => event.currentTarget.select()} />
          <button className="ai-copy-btn" onClick={copy}>
            {copied ? "คัดลอกแล้ว ✓" : "คัดลอกข้อความ"}
          </button>
        </div>
      </section>
    </div>
  );
}

function CandidateTable({ candidates, selectedSymbol, onSelect, onScoreInfo, onCaInfo, onSortChange, dataAsOf }: { candidates: Candidate[]; selectedSymbol: string; onSelect: (stock: Candidate) => void; onScoreInfo: (stock: Candidate) => void; onCaInfo: (stock: Candidate) => void; onSortChange?: () => void; dataAsOf?: string | null }) {
  const [sortBy, setSortBy] = useState<SortKey>("buyVol");
  const [showPrompt, setShowPrompt] = useState(false);

  // Rank every stock within each metric so each cell can show its own #position.
  const ranks = useMemo(() => {
    const result = {} as Record<SortKey, Map<string, number>>;
    (Object.keys(sortValue) as SortKey[]).forEach((key) => {
      result[key] = buildRanks(candidates, sortValue[key]);
    });
    return result;
  }, [candidates]);

  // Switching the sort also refetches fresh data, then re-sorts by the new key.
  const changeSort = (key: SortKey) => {
    setSortBy(key);
    onSortChange?.();
  };

  const sorted = useMemo(() => {
    const getValue = sortValue[sortBy];
    return [...candidates].sort((a, b) => getValue(b) - getValue(a));
  }, [candidates, sortBy]);

  const active = sortOptions.find((option) => option.key === sortBy);
  const relatedSet = new Set(active?.related ?? []);
  // Highlight the sorted column (col-active) + the columns worth reading with it (col-related).
  const colClass = (key: SortKey) => (key === sortBy ? "col-active" : relatedSet.has(key) ? "col-related" : "");

  return (
    <>
      <div className="table-sort">
        <div className="segmented sort-group" role="group" aria-label="เรียงลำดับ">
          {sortOptions.map((option) => (
            <button
              key={option.key}
              className={sortBy === option.key ? "active" : ""}
              title={`เรียงด้วย ${option.label} + ดึงข้อมูลใหม่`}
              onClick={() => changeSort(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <button
          className="ai-prompt-trigger"
          title="สร้างข้อความ (prompt) พร้อมหุ้น 10 อันดับแรก + แหล่งข่าว เพื่อก๊อปไปให้ AI วิเคราะห์ต่อ"
          onClick={() => setShowPrompt(true)}
        >
          <Newspaper size={15} aria-hidden="true" />
          ข้อความสำหรับ AI
        </button>
      </div>

      {showPrompt ? (
        <AiPromptDialog
          rows={sorted.slice(0, 10)}
          sortLabel={active?.label ?? ""}
          dataAsOf={dataAsOf}
          onClose={() => setShowPrompt(false)}
        />
      ) : null}

      {active ? (
        <div className="sort-explain">
          <h4>กำลังเรียงด้วย “{active.label}”</h4>
          <p><strong>คืออะไร</strong> — {active.what}</p>
          <p><strong>ทำไมถึงสำคัญ</strong> — {active.why}</p>
          <p><strong>ควรดูค่าไหนประกอบ</strong> — {active.watch}</p>
        </div>
      ) : null}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th className="rank-col">#</th>
              <th>Symbol</th>
              <th className={colClass("buyVol")}>Buy Vol</th>
              <th className={colClass("gap")}>Gap %</th>
              <th className={colClass("volRatio")}>Vol Ratio</th>
              <th className={colClass("value")}>มูลค่า ฿M</th>
              <th className={colClass("latestVol")}>Vol ล่าสุด</th>
              <th className={colClass("atr")}>ช่วงแกว่ง %</th>
              <th className={colClass("score")}>Score</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((stock, index) => (
              <tr
                key={stock.symbol}
                className={stock.symbol === selectedSymbol ? "selected-row" : ""}
                onClick={() => onSelect(stock)}
              >
                <td className="rank-col">{index + 1}</td>
                <td>
                  <button
                    className="symbol-button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onSelect(stock);
                    }}
                  >
                    {stock.symbol}
                  </button>
                  <XdBadge stock={stock} onClick={onCaInfo} />
                  <span>{stock.sector}</span>
                </td>
                <td className={colClass("buyVol")}>
                  {getPreopenBuyVolumeK(stock) !== null ? <RankBadge rank={ranks.buyVol.get(stock.symbol)} /> : null}
                  {formatVolumeK(getPreopenBuyVolumeK(stock))}
                </td>
                <td className={`${gapClass(stock.preopen.indicativeGapPct)} ${colClass("gap")}`.trim()}>
                  <RankBadge rank={ranks.gap.get(stock.symbol)} />
                  {formatSignedPercent(stock.preopen.indicativeGapPct)}
                </td>
                <td className={colClass("volRatio")}>
                  <RankBadge rank={ranks.volRatio.get(stock.symbol)} />
                  {formatRatio(volumeRatio(stock))}
                </td>
                <td className={colClass("value")}>
                  <RankBadge rank={ranks.value.get(stock.symbol)} />
                  {formatMillionBaht(stock.market.value1dM)}
                </td>
                <td className={colClass("latestVol")}>
                  <RankBadge rank={ranks.latestVol.get(stock.symbol)} />
                  {formatShareVolume(latestVolume(stock))}
                </td>
                <td className={colClass("atr")}>
                  <RankBadge rank={ranks.atr.get(stock.symbol)} />
                  {formatPercent(stock.technical.atrPct)}
                </td>
                <td className={colClass("score")}>
                  <RankBadge rank={ranks.score.get(stock.symbol)} />
                  <button
                    type="button"
                    className={`${scoreClass(stock.score)} score-button`}
                    title="ดูวิธีคำนวณคะแนน"
                    onClick={(event) => {
                      event.stopPropagation();
                      onScoreInfo(stock);
                    }}
                  >
                    {stock.score}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// Date + time of the data itself (exchange last-trade time), shown in SET local time.
function formatDataTime(value: string) {
  return new Intl.DateTimeFormat("th-TH-u-ca-gregory", {
    timeZone: "Asia/Bangkok",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(value));
}
